// ported from https://github.com/Evryway/lir

import { Tools, Vector2, Vector3 } from '@babylonjs/core';

interface IVector2 {
  x: number;
  y: number;
}

export function calculateConcavePolygon(vs: Vector2[]) {
  // step 1: find bottom-right point in vs.
  // minimum y (and optionally maximum x, if two or more min-y points are colinear with x-axis)
  var c = vs.length;

  let p0 = vs[0]!;
  let idx = 0;
  const vs_sorted = [p0];
  for (let i = 1; i < c; i++) {
    const pi = vs[i]!;
    if (pi.y < p0.y || (pi.y == p0.y && pi.x > p0.x)) {
      p0 = pi;
      idx = i;
    }
    vs_sorted.push(pi);
  }

  vs_sorted.splice(idx, 1);
  // at this point, vs_sorted does NOT contain p0 - we're going to put it at the front shortly.

  // sort on radial of vector p0->px.
  vs_sorted.sort((p1, p2) => {
    // 2d cross product. (a,b) , (c,d) -> ad - cb
    // "on the left" (looking from above, anticlockwise)
    // gives a positive value for d.

    var p1x = p1.x - p0.x;
    var p2x = p2.x - p0.x;
    var p1y = p1.y - p0.y;
    var p2y = p2.y - p0.y;

    var d = p1x * p2y - p2x * p1y;
    // leftmost (positive value) sorts lowest.
    var r = -d - 0.0000001; // epsilon to ensure the sort is stable.
    // in the colinear case (p0 -> p1 -> p2 is a straight line, or p0 -> p2 -> p1 is a straight line)
    if (r == 0) {
      var d1 = p1x * p1x + p1y * p1y;
      var d2 = p2x * p2x + p2y * p2y;
      // closest (smallest) sorts lowest.
      r = d1 - d2;
    }
    return r;
  });

  vs_sorted.splice(0, 0, p0);
  return vs_sorted;
}

export function calculateCenterOfMass(vs_src: Vector2[]) {
  let center = Vector2.Zero();
  let area = 0;
  for (let i = 0; i < vs_src.length; i++) {
    const next_i = (i + 1) % vs_src.length;
    const v1 = vs_src[i]!;
    const v2 = vs_src[next_i]!;
    const factor = v1.x * v2.y - v2.x * v1.y;
    area += factor;
    center.addInPlace(new Vector2((v1.x + v2.x) * factor, (v1.y + v2.y) * factor));
  }
  area /= 2.0;
  area *= 6.0;

  const factor = 1.0 / area;
  center = new Vector2(center.x * factor, center.y * factor);

  return center;
}

export function calculateRectangleByCenterOfMass(vs_src: Vector2[], center: Vector2) {
  let minDistanceEdge = Number.MAX_VALUE;
  let nearestEdge = Vector2.Zero();

  for (let i = 0; i < vs_src.length; i++) {
    const next_i = (i + 1) % vs_src.length;
    const v1 = vs_src[i]!;
    const v2 = vs_src[next_i]!;

    const distance = distanceLineAndPoint(v1, v2, center);
    if (distance < minDistanceEdge) {
      minDistanceEdge = distance;
      nearestEdge = v2.subtract(v1);
    }
  }

  const axis = new Vector2(-nearestEdge.y, nearestEdge.x).normalize();

  var size = Vector2.One().scale(minDistanceEdge).scale(Math.sqrt(2));
  return { center, axis, size };
}

export function distanceLineAndPoint(s: Vector2, e: Vector2, p: Vector2) {
  const sp = p.subtract(s);
  const se = e.subtract(s);
  const es = s.subtract(e);
  const ep = p.subtract(e);

  if (Vector2.Dot(sp, se) * Vector2.Dot(es, ep) >= 0) return Math.abs(crossProduct(sp, se) / Vector2.Distance(s, e));
  else return Math.min(Vector2.Distance(s, p), Vector2.Distance(e, p));
}

export function crossProduct(left: Vector2, right: Vector2) {
  return left.x * right.y - left.y * right.x;
}

/**
 * calculate axis-aligned cell array.
 *
 * @param vs ordered polygon points array in vs (does not need to be convex, DOES need to be ordered as a simple polygon)
 */
export function calculateInteriorCells(vs: Vector2[]) {
  const vc = vs.length;

  // optimisation : this is "pick distinct values, sort smallest to largest" - can definitely be
  // done faster for a couple of ms improvement.
  // xs = vs.Select(v => v.x).OrderBy(x => x).Distinct().ToArray();
  // ys = vs.Select(v => v.y).OrderBy(y => y).Distinct().ToArray();

  // this variant clamps with an epsilon, to ensure the math doesn't get too wacky.

  const xsl = [];
  const ysl = [];
  for (const v of vs) {
    xsl.push(v.x);
    ysl.push(v.y);
  }
  xsl.sort();
  ysl.sort();

  // de-dupe - including epsilon.

  let xmin = xsl[0]!;
  let xmax = xsl[xsl.length - 1]!;
  let ymin = ysl[0]!;
  let ymax = ysl[ysl.length - 1]!;
  let mmin = Math.min(xmin, ymin);
  let mmax = Math.max(xmax, ymax);

  const xsd = [xmin];
  const ysd = [ymin];

  const epsilon = (mmax - mmin) / (1024 * 1024); // 1 millionth of the span.

  for (let i = 0; i < vc; i++) {
    const xs0 = xsl[i]!;
    const xs1 = xsl[i + 1]!;
    const ys0 = ysl[i]!;
    const ys1 = ysl[i + 1]!;
    if (xs1 - xs0 > epsilon) xsd.push(xs1);
    if (ys1 - ys0 > epsilon) ysd.push(ys1);
  }

  const xs = xsd as number[];
  const ys = ysd as number[];

  // if we want to, here we can extend xs / ys, by adding additional points (midpoints, or reproject
  // as per the paper)

  const xc = xs.length - 1;
  const yc = ys.length - 1;

  // cells array indicates if the cell at index X,Y (from xs[X] to xs[X+1] and ys[Y] to ys[Y+1])
  // is INTERIOR to the polygon (1) or EXTERIOR. (0 or -1)

  // 1D arrays perform faster than 2D arrays - if performance is critical, use a 1D array
  // and index into it, e.g. (y*xc) + x;
  const cells = [] as number[][];

  // now, iterate the polygon edges. find the x span (and their associated indices in xs) and
  // the y span (and their associated indices in ys)

  let v0 = vs[0]!;
  let six = -1;
  let siy = -1;
  let eix = xs.findIndex((x) => x >= v0.x);
  let eiy = ys.findIndex((y) => y >= v0.y);

  for (let i = 0; i < vc; i++) {
    const s = vs[i]!;
    const e = vs[(i + 1) % vc]!;
    const edge = e.subtract(s);

    // get the indices for the start - it should be the end vertex of the previous edge.
    six = eix;
    siy = eiy;

    // eix = xs.IndexOf(e.x);
    // eiy = ys.IndexOf(e.y);
    // We can use the orientation of the edge to determine the direction of the search,
    // and the end vertex x,y indices should be near in the arrays to the start vertex x,y indices.
    // depending on edge length, etc - no guarantees!
    // could possibly binary search, but if the edge lengths are short, a linear scan should be
    // fairly fast anyway.
    const tx = edge.x >= 0 ? 1 : -1; // -1 or 1
    const ty = edge.y >= 0 ? 1 : -1; // -1 or 1
    if (tx > 0) while (xs[eix]! < e.x && eix < xc) eix++;
    else while (xs[eix]! > e.x && eix > 0) eix--;
    if (ty > 0) while (ys[eiy]! < e.y && eiy < yc) eiy++;
    else while (ys[eiy]! > e.y && eiy > 0) eiy--;

    // we now have a span.
    const span_x_start = Math.min(six, eix);
    const span_y_start = Math.min(siy, eiy);

    const span_x_end = Math.max(six, eix);
    const span_y_end = Math.max(siy, eiy);

    // we care about the edge direction (and hence, the normal direction).

    // when testing a rectangle, we want a relevant corner to check is "inside" the edge.
    // if we pick the right one, we can be sure the other three are also inside - and any that
    // fail mean the rectangle is partially or totally outside the edge.

    // given the winding order of the polygon is COUNTERCLOCKWISE, the "interior" side of the edge
    // is a 90 degree CCW rotation. (orthogonal to the edge, facing into the polygon)
    // I'm calling this "into" - note, this is NOT normalized! (we are only ever doing dot sign checks)
    let into = new Vector2(-edge.y, edge.x);
    let rx = into.x >= 0 ? 0 : 1;
    let ry = into.y >= 0 ? 0 : 1;

    // for -x,-y edges, the into direction is +X, -Y - use the TL cell vertex to test. (0,1)
    // for +x,-y edges, the into direction is +X, +Y - use the BL cell vertex to test. (0,0)
    // for -x,+y edges, the into direction is -X, -Y - use the TR cell vertex to test. (1,1)
    // for +x,+y edges, the into direction is -X, +Y - use the BR cell vertex to test. (1,0)

    // I'm not sure it's impossible for two edges to span the same cell - it may be, in which case
    // the "if interiors[p,q] < 0 continue" tests can be removed, below.

    // this covers all of the cells that the edge crosses.
    // if the edge is vertical or horizontal, the span width in that axis should be 0.
    if (span_x_end - span_x_start == 0) {
      // vertical edge. pick the cells on the interior side.
      var p = span_x_start - rx;
      for (let q = span_y_start; q < span_y_end; q++) {
        if (cells[p]![q]! < 0) continue;
        cells[p]![q] = 1;
      }
      continue;
    } else if (span_y_end - span_y_start == 0) {
      // horizontal edge. pick the cells on the interior side.
      var q = span_y_start - ry;
      for (let p = span_x_start; p < span_x_end; p++) {
        if (cells[p]![q]! < 0) continue;
        cells[p]![q] = 1;
      }
      continue;
    }
    for (let q = span_y_start; q < span_y_end; q++) {
      for (let p = span_x_start; p < span_x_end; p++) {
        // if we've already marked this as exterior, then skip it.
        // it's possible to be interior to another edge, but still exterior
        // to this one, so continue the check in that case.
        if (cells[p]![q]! < 0) continue;
        // based on the edge direction, pick the correct corner to test against.
        var v = new Vector2(xs[p + rx], ys[q + ry]);
        var sv = v.subtract(s);
        var d = Vector2.Dot(sv, into);

        // mark the cell either exterior (-1) or interior (1)
        cells[p]![q] = d < 0 ? -1 : 1;
      }
    }
  }

  // outside region sweep.
  // some cells may not be spanned by edges.
  // start on the outside of the region, and mark everything as exterior, until
  // we come across a cell that has been explicitly marked.

  for (let q = 0; q < yc; q++) {
    // from the left edge.
    for (let x = 0; x < xc; x++) {
      if (cells[x]![q] != 0) break;
      cells[x]![q] = -1;
    }
    // from the right edge.
    for (let x = xc - 1; x >= 0; x--) {
      if (cells[x]![q] != 0) break;
      cells[x]![q] = -1;
    }
  }

  for (let p = 0; p < xc; p++) {
    // from the bottom edge.
    for (let y = 0; y < yc; y++) {
      if (cells[p]![y] != 0) break;
      cells[p]![y] = -1;
    }
    // from the top edge.
    for (let y = yc - 1; y >= 0; y--) {
      if (cells[p]![y] != 0) break;
      cells[p]![y] = -1;
    }
  }

  // sweep for interior (untested) cells.
  // in fact, mark everything.
  for (let j = 0; j < yc; j++) {
    for (let i = 0; i < xc; i++) {
      // anything that was -1 goes to 0.
      // anything that was 0 or 1 goes to 1.
      // this ensures any un-tested cells are classed as interior.
      cells[i]![j] = cells[i]![j]! < 0 ? 0 : 1;
    }
  }

  return {
    xs,
    ys,
    cells,
  };
}

/**
 * take the outputs from {@link calculateInteriorCells}, and calculate the rectangular region that has the largest area.
 *
 * @param xs
 * @param ys
 * @param cells
 */
export function calculateLargestInterioRectangle(xs: number[], ys: number[], cells: number[][]) {
  // cell lengths. interiors[x,y] should match [axc,ayc];
  const axc = xs.length - 1;
  const ayc = ys.length - 1;

  let best_area = 0;
  let best_origin = new Vector2(-1, -1);
  let best_span = new Vector2(-1, -1);

  const hspans = [];
  const vspans = [];

  var lengths_horizontal = [] as number[][];
  var lengths_vertical = [] as number[][];

  for (let y = 0; y < ayc; y++) {
    let span = 0;
    for (let x = axc - 1; x >= 0; x--) {
      span = cells[x]![y]! <= 0 ? 0 : span + xs[x + 1]! - xs[x]!;
      lengths_horizontal[x]![y] = span;
    }
  }

  for (let x = 0; x < axc; x++) {
    let span = 0;
    for (let y = ayc - 1; y >= 0; y--) {
      span = cells[x]![y]! <= 0 ? 0 : span + ys[y + 1]! - ys[y]!;
      lengths_vertical[x]![y] = span;
    }
  }

  /* IF REQUIRED FOR TESTING, CALCULATE ADJACENCY.
    var adjacency_horizontal = new int[axc, ayc];
    var adjacency_vertical = new int[axc, ayc];
    for (let y = 0; y < ayc; y++)
    {
        int adj = 0;
        for (let x = axc - 1; x >= 0; x--)
        {
            adj = (cells[x, y] <= 0) ? 0 : adj + 1;
            adjacency_horizontal[x, y] = adj;
        }
    }

    for (let x = 0; x < axc; x++)
    {
        int adj = 0;
        for (let y = ayc - 1; y >= 0; y--)
        {
            adj = (cells[x, y] <= 0) ? 0 : adj + 1;
            adjacency_vertical[x, y] = adj;
        }
    }
  */

  for (let y = 0; y < ayc; y++) {
    for (let x = 0; x < axc; x++) {
      const iv = cells[x]![y]!;
      if (iv == 0) continue;

      var h = lengths_horizontal[x]![y]!;
      var v = lengths_vertical[x]![y]!;

      // if the best POSSIBLE area (which may not be valid!)
      // is smaller than the best area, then we don't need to run any further tests.
      if (h * v < best_area) continue;

      // generate H vector - this is horizontal spans for each step up.
      hspans.length = 0;
      // look at horizontal spans.
      // step up from our initial cell, and look right.

      hspans.push(h);
      for (let q = y + 1; q < ayc; q++) {
        if (cells[x]![q] == 0) break;
        var h2 = lengths_horizontal[x]![q]!;
        if (h2 >= h) continue;
        h = h2;
        hspans.push(h);
      }

      // generate V vector. This is vertical spans for each step right.
      vspans.length = 0;
      // look at vertical spans.
      // step right from our initial cell, and look up.

      vspans.push(v);
      for (let p = x + 1; p < axc; p++) {
        if (cells[p]![y] == 0) break;
        var v2 = lengths_vertical[p]![y]!;
        if (v2 >= v) continue;
        v = v2;

        vspans.push(v);
      }
      // log the vectors.
      //var hstr = string.Join(", ", hspans.Select(ht => ht.ToString()));
      //var vstr = string.Join(", ", vspans.Select(vt => vt.ToString()));
      //Debug.Log($"node ({x}, {y}) : H = ({hstr}), V = ({vstr})");

      if (vspans.length != hspans.length) {
        console.error(new Error(`span counts don't match. ${hspans.length} ${vspans.length}`));

        /*

                        // run the adjacency code instead.
                        // THIS IS TESTING CODE. DO NOT USE THIS IN PRODUCTION - RATHER
                        // FIX THE ISSUE SO IT'S NOT REQUIRED.

                        // generate H vector - this is horizontal adjacency for each step up.
                        clir_hvec.Clear();
                        // look at horizontal adjacency.
                        // step up from our initial cell, and look right.

                        var sh = adjacency_horizontal[x, y];
                        clir_hvec.Add(sh);
                        for (let q = y + 1; q < ayc; q++)
                        {
                            if (cells[x, q] != 1) break;
                            // each row can only be as large as the previous - a rectangle cannot push
                            // further out than a lower row.
                            sh = Mathf.Min(adjacency_horizontal[x, q], sh);
                            clir_hvec.Add(sh);
                        }

                        // generate V vector. This is vertical adjacency for each step right.
                        clir_vvec.Clear();
                        // look at vertical adjacency.
                        // step right from our initial cell, and look up.

                        var sv = adjacency_vertical[x, y];
                        clir_vvec.Add(sv);
                        for (let p = x + 1; p < axc; p++)
                        {
                            if (cells[p, y] != 1) break;
                            // each column can only be as large as the previous - a rectangle cannot push
                            // further up than a previous column.
                            sv = Mathf.Min(adjacency_vertical[p, y], sv);
                            clir_vvec.Add(sv);
                        }

                        clir_spans.Clear();

                        // generate the set of valid spans.
                        int2 span_last = new int2(-1, -1);
                        for (let i = 0; i < clir_hvec.Count; i++)
                        {
                            int p = clir_hvec[i];
                            int q = clir_vvec[p - 1];
                            int2 span = new int2(p, q);
                            if (span.x != span_last.x && span.y != span_last.y)
                            {
                                clir_spans.Add(span);
                                span_last = span;
                            }
                        }

                        for (let i = 0; i < clir_spans.Count; i++)
                        {
                            var span = clir_spans[i];
                            var xstart = xs[x];
                            var xend = xs[x + span.x];
                            var ystart = ys[y];
                            var yend = ys[y + span.y];
                            var xsize = xend - xstart;
                            var ysize = yend - ystart;
                            var area = xsize * ysize;
                        }

                        */

        continue;
      }

      // reverse the v spans list - this lets us trivially combine the correct
      // spans for each rectangle combination with the same list index.
      vspans.reverse();

      for (let i = 0; i < hspans.length; i++) {
        const hl = hspans[i]!;
        const vl = vspans[i]!;
        const area = hl * vl;
        if (area > best_area) {
          best_area = area;
          best_origin = new Vector2(xs[x], ys[y]);
          best_span = new Vector2(hl, vl);
        }
      }
    }
  }

  if (best_area <= 0) return;

  const center = best_origin.add(best_span.scale(0.5));
  const size = best_span;
  // console.debug(`best area : ${best_area} ${center} ${size}`);

  //Debug.Log($"X : {xstart} {xend}, Y : {ystart} {yend}");
  //Debug.Log($"Centre: {centre.F3()} size : {size.F3()}");

  return {
    center,
    axis: new Vector2(1, 0), // right,
    size,
  };
}

/**
 * this version does ONE lot of allocations, and tests across angles too.
 * scans from -45 to +45 degrees in angle_step increments.
 * optionally (see use_fan) splits the angle search up to give wider
 * coverage of angle options earlier, so quick reject gets more opportunity
 * to work.
 * @param vs_src
 * @param angle_step
 */
export function calculateLargestInteriorRectangleWithAngleSweep(vs_src: Vector2[], angle_step: number) {
  const vc = vs_src.length;
  if (vc < 4) {
    return;
  }
  const vs = [] as Vector2[];

  const xs = [];
  const ys = [];

  const xds = [];
  const yds = [];

  const xsl = [];
  const ysl = [];

  const hspans = [];
  const vspans = [];

  const cc = vc * vc;

  const lengths_horizontal = new Array(cc).fill(0) as number[];
  const lengths_vertical = new Array(cc).fill(0) as number[];

  const cells = new Array(cc).fill(0) as number[];

  let best_area = 0;
  let best_angle = 0;
  let best_origin = new Vector2(-1, -1);
  let best_span = new Vector2(-1, -1);

  let ang_min = -45;
  let ang_max = 45;
  let ang_range = ang_max - ang_min;

  let steps_lin = [];
  for (let f = ang_min; f < ang_max; f += angle_step) steps_lin.push(f);

  var steps = steps_lin;

  const use_fan = true;

  if (use_fan) {
    // divide and conquer the angle steps - should hopefully
    // hone in on a large value faster than just a linear sweep,
    // which should give faster quick reject.

    // ok - that's all of our steps in an array.
    // next, start pulling them out in layers (POT)
    // so we get 1, 2, 4, 8, 16, etc.

    const step_count = steps_lin.length;
    let steps_at_layer = [];
    let pull = 1;
    let steps_left = step_count;
    while (steps_left > 0) {
      steps_at_layer.push(pull);
      steps_left -= pull;
      pull = Math.min(steps_left, pull << 1);
    }

    const steps_fan = [] as number[];
    for (var lay of steps_at_layer) {
      // pull out lay items from steps_lin.
      let range_per_item = ang_range / lay;
      let mid_per_item = range_per_item * 0.5;
      let next = ang_min;
      for (let i = 0; i < lay; i++) {
        let check = next + mid_per_item;

        let idx = steps_lin.indexOf(check);
        if (idx < 0) idx = Math.min(Math.max(~idx + 1, 0), steps_lin.length - 1);
        var val = steps_lin[idx]!;
        steps_fan.push(val);
        steps_lin.splice(idx, 1);
        next += range_per_item;
      }
    }
    steps = steps_fan;
  }

  // work out the number of layers, where we have 1 right at the top.

  //float angle = 0f;
  for (const angle of steps) {
    //Debug.Log(angle);

    xsl.length = 0;
    ysl.length = 0;

    for (let i = 0; i < vc; i++) {
      if (!vs[i]) vs[i] = Vector2.Zero();
      const v = vs_src[i]!.rotateToRef(Tools.ToRadians(angle), vs[i]!);
      xsl.push(v.x);
      ysl.push(v.y);
    }

    xsl.sort((a, b) => a - b);
    ysl.sort((a, b) => a - b);

    const xmin = xsl[0]!;
    const xmax = xsl[xsl.length - 1]!;
    const ymin = ysl[0]!;
    const ymax = ysl[ysl.length - 1]!;
    const mmin = Math.min(xmin, ymin);
    const mmax = Math.max(xmax, ymax);
    const epsilon = (mmax - mmin) / (1024 * 1024); // 1 millionth of the span.

    let xc = 1;
    let yc = 1;
    xs[0] = xsl[0];
    ys[0] = ysl[0];

    for (let i = 1; i < vs.length; i++) {
      if (xsl[i]! - xsl[i - 1]! > epsilon) xs[xc++] = xsl[i];
      if (ysl[i]! - ysl[i - 1]! > epsilon) ys[yc++] = ysl[i];
    }

    for (let i = 0; i < xc - 1; i++) xds[i] = xs[i + 1]! - xs[i]!;
    for (let i = 0; i < yc - 1; i++) yds[i] = ys[i + 1]! - ys[i]!;

    xc--;
    yc--;

    cells.fill(0);

    var v0 = vs[0]!;

    var eix = 0;
    while (xs[eix]! < v0.x && eix < xc) eix++;
    var eiy = 0;
    while (ys[eiy]! < v0.y && eiy < yc) eiy++;
    // in this case, it may be slightly faster - but not worth the algo complexity.
    //var eix = System.Array.BinarySearch(xs, v0.x); if (eix < 0) eix = Mathf.Clamp(0,xc+1,(~eix) - 1);
    //var eiy = System.Array.BinarySearch(ys, v0.y); if (eix < 0) eiy = Mathf.Clamp(0,yc+1,(~eiy) - 1);
    for (let i = 0; i < vc; i++) {
      const s = vs[i]!;
      const e = vs[(i + 1) % vc]!;
      const edge = e.subtract(s);

      const into = new Vector2(-edge.y, edge.x);
      let rx = into.x >= 0 ? 0 : 1;
      let ry = into.y >= 0 ? 0 : 1;

      var six = eix;
      var siy = eiy;

      let tx = edge.x >= 0 ? 1 : -1; // -1 or 1
      let ty = edge.y >= 0 ? 1 : -1; // -1 or 1

      if (tx > 0) {
        while (xs[eix]! < e.x && eix <= xc) eix++;
      } else {
        while (xs[eix]! > e.x && eix > 0) eix--;
      }
      if (ty > 0) {
        while (ys[eiy]! < e.y && eiy <= yc) eiy++;
      } else {
        while (ys[eiy]! > e.y && eiy > 0) eiy--;
      }

      // tried it. timed it. much slower. probably because of locality in the whiles, above.
      //int wx = edge.x >= 0 ? 0 : 1;
      //int wy = edge.y >= 0 ? 0 : 1;
      //var eixb = System.Array.BinarySearch(xs, e.x); if (eixb < 0) { eixb = Mathf.Clamp((~eixb) - wx, 0, xc + 1); }
      //var eiyb = System.Array.BinarySearch(ys, e.y); if (eiyb < 0) { eiyb = Mathf.Clamp((~eiyb) - wy, 0, yc + 1); }

      // we now have a span.
      const span_x_start = Math.min(six, eix);
      const span_y_start = Math.min(siy, eiy);

      const span_x_end = Math.max(six, eix);
      const span_y_end = Math.max(siy, eiy);

      if (span_x_end - span_x_start == 0) {
        const p = span_x_start - rx;
        for (let q = span_y_start; q < span_y_end; q++) {
          const ci = p * vc + q;
          if (cells[ci]! < 0) continue;
          cells[ci] = 1;
        }
        continue;
      } else if (span_y_end - span_y_start == 0) {
        const q = span_y_start - ry;
        for (let p = span_x_start; p < span_x_end; p++) {
          const ci = p * vc + q;
          if (cells[ci]! < 0) continue;
          cells[ci] = 1;
        }
        continue;
      }
      for (let q = span_y_start; q < span_y_end; q++) {
        for (let p = span_x_start; p < span_x_end; p++) {
          const ci = p * vc + q;
          if (cells[ci]! < 0) continue;
          const v = new Vector2(xs[p + rx], ys[q + ry]);
          const sv = v.subtract(s);
          const d = Vector2.Dot(sv, into);
          cells[ci] = d < 0 ? -1 : 1;
        }
      }
    }

    // outside region sweep.
    // some cells may not be spanned by edges.
    // start on the outside of the region, and mark everything as exterior, until
    // we come across a cell that has been explicitly marked.

    for (let q = 0; q < yc; q++) {
      for (let x = 0; x < xc; x++) {
        const ci = x * vc + q;
        if (cells[ci] != 0) break;
        cells[ci] = -1;
      }
      for (let x = xc - 1; x >= 0; x--) {
        const ci = x * vc + q;
        if (cells[ci] != 0) break;
        cells[ci] = -1;
      }
    }

    for (let p = 0; p < xc; p++) {
      for (let y = 0; y < yc; y++) {
        const ci = p * vc + y;
        if (cells[ci] != 0) break;
        cells[ci] = -1;
      }
      for (let y = yc - 1; y >= 0; y--) {
        const ci = p * vc + y;
        if (cells[ci] != 0) break;
        cells[ci] = -1;
      }
    }

    // sweep for interior (untested) cells.
    // in fact, mark everything.
    for (let j = 0; j < yc; j++) {
      for (let i = 0; i < xc; i++) {
        const ci = i * vc + j;
        // anything that was -1 goes to 0.
        // anything that was 0 or 1 goes to 1.
        // this ensures any un-tested cells are classed as interior.
        cells[ci] = cells[ci]! < 0 ? 0 : 1;
      }
    }

    for (let y = 0; y < yc; y++) {
      let span = 0;
      for (let x = xc - 1; x >= 0; x--) {
        const ci = x * vc + y;
        span = cells[ci]! <= 0 ? 0 : span + xds[x]!;
        lengths_horizontal[ci] = span;
      }
    }

    for (let x = 0; x < xc; x++) {
      let span = 0;
      for (let y = yc - 1; y >= 0; y--) {
        const ci = x * vc + y;
        span = cells[ci]! <= 0 ? 0 : span + yds[y]!;
        lengths_vertical[ci] = span;
      }
    }

    for (let y = 0; y < yc; y++) {
      for (let x = 0; x < xc; x++) {
        const ci = x * vc + y;
        const iv = cells[ci]!;
        if (iv == 0) continue;

        let h = lengths_horizontal[ci]!;
        let v = lengths_vertical[ci]!;
        if (h * v < best_area) continue;

        let hsc = 1;
        hspans[0] = h;
        for (let q = y + 1; q < yc; q++) {
          const ci = x * vc + q;
          if (cells[ci] == 0) break;
          const h2 = lengths_horizontal[ci]!;
          if (h2 >= h) continue;
          h = h2;
          hspans[hsc++] = h;
        }

        let vsc = 1;
        vspans[0] = v;
        for (let p = x + 1; p < xc; p++) {
          const ci = p * vc + y;
          if (cells[ci] == 0) break;
          var v2 = lengths_vertical[ci]!;
          if (v2 >= v) continue;
          v = v2;
          vspans[vsc++] = v;
        }

        if (hsc != vsc) {
          console.debug(`span counts don't match. ${hsc} ${vsc}`);
          continue;
        }

        for (let i = 0; i < hsc; i++) {
          const hl = hspans[i]!;
          const vl = vspans[hsc - (i + 1)]!;
          const area = hl * vl;
          if (area > best_area) {
            best_area = area;
            best_origin = new Vector2(xs[x], ys[y]);
            best_span = new Vector2(hl, vl);
            best_angle = angle;
          }
        }
      }
    }
  }

  if (best_area <= 0) {
    return;
  }

  const radian = Tools.ToRadians(-best_angle);
  const bo_r = best_origin.rotateToRef(radian, Vector2.Zero());
  const bs_r = best_span.rotateToRef(radian, Vector2.Zero());

  const center = bo_r.add(bs_r.scale(0.5));
  const size = best_span;
  const axis = new Vector2(1, 0).rotateToRef(radian, Vector2.Zero()).normalize();

  return {
    center,
    size,
    axis,
  };
}

export function calculateInteriorBound(coordinates: Vector3[]) {
  const vs = coordinates
    .map((t) => new Vector2(t.x, t.z))
    .filter((value, index, self) => self.findIndex((v) => v.equalsWithEpsilon(value)) === index);

  if (vs.length < 3) return;

  const bound = (() => {
    if (vs.length > 4) {
      return calculateLargestInteriorRectangleWithAngleSweep(vs, 5);
    } else if (vs.length == 4) {
      const l1 = vs[1]!.subtract(vs[0]!);
      const l2 = vs[2]!.subtract(vs[1]!);
      const l3 = vs[3]!.subtract(vs[2]!);
      const l4 = vs[0]!.subtract(vs[3]!);
      const a = l1.length() < l3.length() ? l1 : l3;
      const b = l2.length() < l4.length() ? l2 : l4;
      const aLength = a.length();
      const bLength = b.length();
      const axis = aLength > bLength ? a : b;
      const sizeA = aLength > bLength ? aLength : bLength;
      const sizeB = aLength > bLength ? bLength : aLength;

      const sortedVs = calculateConcavePolygon(vs);
      const center = calculateCenterOfMass(sortedVs);

      // var playground = this as DanjiFacilityPlayGround;
      // if (playground == null)
      return {
        center,
        axis: axis.normalize(),
        size: new Vector2(sizeA, sizeB),
      };
      // else
      // return calculateRectangleByCenterOfMass(vs, center);
    } else {
      const subdividedVs = [];
      for (let i = 0; i < vs.length; i++) {
        const current = vs[i]!;
        const next = vs[(i + 1) % vs.length]!;
        const midpoint = current.add(next).scale(0.5);
        subdividedVs.push(current, midpoint);
      }
      return calculateLargestInteriorRectangleWithAngleSweep(subdividedVs, 5);
    }
  })();

  if (!bound) return;

  // Bound2D.AlignMajor
  if (bound.size.x >= bound.size.y) {
    bound.size.set(bound.size.y, bound.size.x);
  } else {
    bound.axis.set(-bound.axis.y, bound.axis.x);
  }
  if (bound.axis.x <= 0) {
    bound.axis.negateInPlace();
  }

  return bound;
}
